import { useMemo, useState } from 'react'

import { EmbedCategoryHint } from 'components/common/Embed/embedCategoryHint'
import { Nullable } from 'models/helpers'
import { Path } from 'constants/router'
import constate from 'constate'
import { useBkbnSearchParams } from 'utils/hooks/useBkbnSearchParams'
import { useEmbedIsOneSystem } from 'utils/embedSession/useEmbedIsOneSystem'
import { useEmbedLocaleSelfHeal } from 'utils/hooks/useEmbedLocaleSelfHeal'
import { useMatch } from 'react-router-dom'

export const [EmbedConfigProvider, useEmbedConfig] = constate(() => {
  const params = useBkbnSearchParams()

  // useMatch (not pathname equality) so Propstack-controlled URLs with a trailing slash still count as embed
  const isEmbed = useMatch(Path.EMBED_PURCHASE_FLOW) !== null
  const revenueChannel = useMemo<'PROPSTACK' | null>(() => isEmbed ? 'PROPSTACK' : null, [isEmbed])
  // Session-first (the URL param is only the rollout fallback) — see useEmbedIsOneSystem.
  const sessionOneSystem = useEmbedIsOneSystem()
  const isOneSystem = useMemo(() => isEmbed && sessionOneSystem, [isEmbed, sessionOneSystem])
  const locale = useMemo(() => params.get('lang'), [params])

  // The service picked on the value-proposition overlay (ISSUE-2017). Deliberately session state
  // (not a URL param): a param would let Propstack deep-link straight past the overlay and its
  // data-transfer notice. The Product step consumes it once to pre-select its filters.
  const [categoryHint, setCategoryHint] = useState<Nullable<EmbedCategoryHint>>(null)

  // Locale self-healing (embed only): the forced ?lang is applied at boot-time by the language detector,
  // but SPA-navigating away to the main app and back re-applies the profile language and nothing re-forces
  // ?lang. Heal on mount AND subscribe to i18n's languageChanged so an in-session drift (i18n.language is
  // not reactive) is corrected the moment it happens, keeping the iframe in the Propstack-controlled
  // language (also realigns moment for date formatting). The warn makes a wrong-language embed diagnosable.
  useEmbedLocaleSelfHeal(isEmbed, locale)

  return { isEmbed, revenueChannel, isOneSystem, locale, categoryHint, setCategoryHint }
})
